import { keyBy } from 'lodash';
import { HorizontalSplitOutlinedIcon, ViewQuiltOutlinedIcon } from 'tuui';
import { FormField, RadioGroup, RadioIcon } from 'taboola-ultimate-ui';
import { useTypeSubtext } from 'hooks';
import { FORM_MODES } from 'modules/campaigns/config';
import { CREATIVE_TYPES } from 'modules/campaigns/modules/creative-display/config/displayTypes';
import { hasNoItems } from 'modules/campaigns/modules/creative-display/utils/hasNoItems';
import { withIndication } from 'modules/errors';
import { useFormDataContext, useFormFieldValue } from 'modules/taboola-common-frontend-modules/formData';
import { GTM_EVENTS } from 'modules/taboola-common-frontend-modules/gtmTracker';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { useFormValidatedValue, validationFunctions } from 'modules/taboola-common-frontend-modules/validations';
import { CampaignCreativeTypeTooltip } from './CampaignCreativeTypeTooltip/CampaignCreativeTypeTooltip';
import styles from './campaignCreativeType.module.scss';

const validations = [
    {
        validationFn: value => validationFunctions.isNotEmpty(value),
        messageId: 'campaign.creative.type.error.message',
        defaultMessage: 'Please select a ad type to continue',
    },
];

const configs = [
    {
        id: CREATIVE_TYPES.DISPLAY,
        icon: ViewQuiltOutlinedIcon,
        label: 'Display',
        subtext: '[BETA] High-visibility display ads for prospecting and engagement.',
    },
    {
        id: CREATIVE_TYPES.NATIVE,
        icon: HorizontalSplitOutlinedIcon,
        label: 'Native',
        subtext: 'Ads designed to match the website’s natural content style and format',
    },
];

const configByValue = keyBy(configs, 'value');

const RadioGroupWithIndication = withIndication(RadioGroup);

export const CampaignCreativeType = () => {
    const { mode } = useFormDataContext();
    const {
        value: campaignAdType,
        onChange: onCampaignAdTypeChange,
        indicationData,
        scrollRef,
    } = useFormValidatedValue({
        field: 'eligibilityType',
        validations,
    });
    const { value: campaignItemType } = useFormFieldValue({ field: 'campaignItemType' });
    const msgPrefix = 'campaign.creative.type';
    const { subtext, setHovered } = useTypeSubtext(campaignAdType, configByValue, msgPrefix);
    const isDisabled = mode !== FORM_MODES.CREATE && !hasNoItems(campaignItemType);
    return (
        <div className={styles['container']}>
            <FormField
                inputId="creative-type"
                label={
                    <FormattedMessage id="campaign.creative.type.title" defaultMessage="Ad type you expect to use" />
                }
                description={
                    <FormattedMessage
                        id="campaign.creative.type.description"
                        defaultMessage="Select one ad type. Please note, this cannot be changed after adding ads to the campaign. "
                    />
                }
                helpText={<CampaignCreativeTypeTooltip />}
                ref={scrollRef}
                subtext={subtext}
            >
                <RadioGroupWithIndication
                    id="creativeType"
                    name="creative-type"
                    className={styles['group']}
                    selectedValue={campaignAdType}
                    onChange={onCampaignAdTypeChange}
                    data-metrics-component="CreativeType"
                    disabled={isDisabled}
                    {...indicationData}
                >
                    {configs.map(({ id, icon: Icon }) => (
                        <RadioIcon
                            key={id}
                            value={id}
                            title={<FormattedMessage id={`campaign.creative.type.title.${id}`} />}
                            iconComponent={<Icon />}
                            labelClassName={styles['radio']}
                            data-metrics-event-name={GTM_EVENTS.USABILITY}
                            data-metrics-value={id}
                            titleClassName={styles['title']}
                            onMouseEnter={() => setHovered && setHovered(id)}
                            onMouseLeave={() => setHovered && setHovered(null)}
                        />
                    ))}
                </RadioGroupWithIndication>
            </FormField>
        </div>
    );
};
